import { useOverlapConfigurationsApi } from "../api/overlap_configurations_api";
import { InstallationType, NullableNumber, SpringType } from "../core/common_types";
import { Accessorio, AccessorioInConfigurazione, AiringOptions, DoorConfig, DoorConfiguration, DoorConfigurationConditions, DoorOption, FormAspettoModel, FormDimensioniModel, HandleConfiguration, HandleOptions, QuoteSummary, AiringConfiguration, ExternalDoorConfiguration, WindowingConfiguration, ExternalDoorOptions, IntegratedDoorConfiguration, IntegratedDoorOptions, TelaioPerimetraleConfiguration } from "../models/door_configuration";
import { User } from "../models/user";

export const useOverlapConfigurationsService = () => {
    const overlapConfigurationsApi = useOverlapConfigurationsApi();

    return {
        async create(currentUser: User, categoryId: number, customerId: NullableNumber, quoteId: NullableNumber): Promise<DoorConfiguration> {
            let result = await overlapConfigurationsApi.create(currentUser, categoryId, customerId, quoteId);

            if (!result.success) {
                throw result.message;
            }

            if (!result.data) throw "Configuration is null";

            return result.data;
        },

        async delete(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.delete(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async loadDoorConfiguration(currentUser: User, configurationId: number): Promise<DoorConfiguration> {
            let result = await overlapConfigurationsApi.get(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            if (!result.data) throw "Configuration is null";

            return result.data;
        },

        async updateDimensions(currentUser: User, configurationId: number, model: FormDimensioniModel): Promise<any> {
            let result = await overlapConfigurationsApi.putDimensions(currentUser, configurationId, model);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async updateAspectOver(currentUser: User, configurationId: number, model: FormAspettoModel): Promise<DoorConfig> {
            let result = await overlapConfigurationsApi.putAspectOver(currentUser, configurationId, model);

            if (!result.success) {
                throw result.message;
            }

            return result.data as DoorConfig;
        },

        async cercaOpzioniPorta(currentUser: User, configuration: DoorConfiguration): Promise<DoorOption[]> {
            let result = await overlapConfigurationsApi.findDoorsOptions(currentUser, configuration);

            if (!result.success) {
                throw result.message;
            }

            return result.data as DoorOption[];
        },

        async findCompatibleMotorizations(currentUser: User, configurationId: number): Promise<Accessorio[]> {
            let result = await overlapConfigurationsApi.findCompatibleMotorizations(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as Accessorio[];
        },

        async findCompatibleGrids(currentUser: User, configurationId: number): Promise<AiringOptions> {
            let result = await overlapConfigurationsApi.findCompatibleGrids(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as AiringOptions;
        },

        async findCompatibleIntegratedDoorAccessories(currentUser: User, configurationId: number): Promise<IntegratedDoorOptions> {
            let result = await overlapConfigurationsApi.findCompatibleIntegratedDoorAccessories(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as IntegratedDoorOptions;
        },

        async findCompatibleExternalDoorAccessories(currentUser: User, configurationId: number): Promise<ExternalDoorOptions> {
            let result = await overlapConfigurationsApi.findCompatibleExternalDoorAccessories(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as ExternalDoorOptions;
        },

        async getMotorizzazione(currentUser: User, configurationId: number): Promise<NullableNumber> {
            let result = await overlapConfigurationsApi.getMotorization(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async setMotorizzazione(currentUser: User, configurationId: number, idMotorizzazione: number): Promise<any> {
            let result = await overlapConfigurationsApi.putMotorization(currentUser, configurationId, idMotorizzazione);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async setPortinaIntegrata(currentUser: User, configurationId: number, data: IntegratedDoorConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putIntegratedDoorConfiguration(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async removePortinaIntegrata(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.removeIntegratedDoorConfiguration(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async setPortinaAffiancata(currentUser: User, configurationId: number, data: ExternalDoorConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putExternalDoorConfiguration(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async removePortinaAffiancata(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.removeExternalDoorConfiguration(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async setFinestratura(currentUser: User, configurationId: number, data: WindowingConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putWindowing(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async removeFinestratura(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.removeWindowing(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async getVentilazione(currentUser: User, configurationId: number): Promise<AiringConfiguration> {
            let result = await overlapConfigurationsApi.getAiring(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as AiringConfiguration;
        },

        async setVentilazione(currentUser: User, configurationId: number, data: AiringConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putAiring(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async removeVentilazione(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.removeAiring(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async getAccessoriMotorizzazione(currentUser: User, configurationId: number): Promise<AccessorioInConfigurazione[]> {
            let result = await overlapConfigurationsApi.getMotorizationAccessories(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as AccessorioInConfigurazione[];
        },

        async findGenericAccessories(currentUser: User, configurationId: number): Promise<Accessorio[]> {
            let result = await overlapConfigurationsApi.findGenericAccessories(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as Accessorio[];
        },

        async getAccessoriGenerici(currentUser: User, configurationId: number): Promise<AccessorioInConfigurazione[]> {
            let result = await overlapConfigurationsApi.getGenericAccessories(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as AccessorioInConfigurazione[];
        },

        async addAccessorio(currentUser: User, configurationId: number, idAccessorio: number, qty: number, value: string|null|undefined): Promise<any> {
            let result = await overlapConfigurationsApi.putAccessory(currentUser, configurationId, idAccessorio, qty, value);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async removeAccessorio(currentUser: User, configurationId: number, idAccessorio: number): Promise<any> {
            let result = await overlapConfigurationsApi.deleteAccessory(currentUser, configurationId, idAccessorio);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async findOpzioniManigliere(currentUser: User, configurationId: number): Promise<HandleOptions> {
            let result = await overlapConfigurationsApi.findHandleOptions(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as HandleOptions;
        },

        async getManigliere(currentUser: User, configurationId: number): Promise<HandleConfiguration | null> {
            let result = await overlapConfigurationsApi.getHandle(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as HandleConfiguration | null;
        },

        async setManigliere(currentUser: User, configurationId: number, data: HandleConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putHandle(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async findOpzioniTelaioPerimetrale(currentUser: User, configurationId: number): Promise<Accessorio[]> {
            let result = await overlapConfigurationsApi.findTelaioPerimetraleOptions(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as Accessorio[];
        },

        async getTelaioPerimetrale(currentUser: User, configurationId: number): Promise<TelaioPerimetraleConfiguration> {
            let result = await overlapConfigurationsApi.getTelaioPerimetrale(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as TelaioPerimetraleConfiguration;
        },

        async setTelaioPerimetrale(currentUser: User, configurationId: number, data: TelaioPerimetraleConfiguration): Promise<any> {
            let result = await overlapConfigurationsApi.putTelaioPerimetrale(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async getCondizioni(currentUser: User, configurationId: number): Promise<DoorConfigurationConditions> {
            let result = await overlapConfigurationsApi.getConditions(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as DoorConfigurationConditions;
        },

        async build(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.build(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async aggiornaCondizioni(currentUser: User, configurationId: number, data: DoorConfigurationConditions): Promise<DoorConfigurationConditions> {
            let result = await overlapConfigurationsApi.updateConditions(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data as DoorConfigurationConditions;
        },

        async setCondizioni(currentUser: User, configurationId: number, data: DoorConfigurationConditions): Promise<DoorConfigurationConditions> {
            let result = await overlapConfigurationsApi.setConditions(currentUser, configurationId, data);

            if (!result.success) {
                throw result.message;
            }

            return result.data as DoorConfigurationConditions;
        },

        async getQuote(currentUser: User, configurationId: number): Promise<QuoteSummary> {
            let result = await overlapConfigurationsApi.getQuote(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data as QuoteSummary;
        },

        async resetQuote(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.resetQuote(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async sendOrder(currentUser: User, configurationId: number): Promise<any> {
            let result = await overlapConfigurationsApi.sendOrder(currentUser, configurationId);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async savePreview(currentUser: User, configurationId: number, preview: File): Promise<any> {
            let result = await overlapConfigurationsApi.savePreview(currentUser, configurationId, preview);

            if (!result.success) {
                throw result.message;
            }

            return result.data;
        },

        async verificaVincoliDimensioni(configuration: DoorConfiguration | null): Promise<any> {
            if (!configuration) throw {severity: "error", message: "Nessuna configurazione in corso"};

            checkGenerali(configuration);
            checkInstallazioneInLuce(configuration);
            checkInstallazioneOltreLuce(configuration);
        },
    }
}

function checkGenerali(configuration: DoorConfiguration) {
    const {
        larghezzaForo,
        altezzaForo,
        tipoInstallazioneMontanteDx,
        tipoInstallazioneMontanteSx,
        larghezzaSpallettaDx,
        larghezzaSpallettaSx
    } = configuration;

    const larghezzaTotale = larghezzaForo
        + (tipoInstallazioneMontanteSx == InstallationType.oltre_luce ? (larghezzaSpallettaSx ?? 0) : 0)
        + (tipoInstallazioneMontanteDx == InstallationType.oltre_luce ? (larghezzaSpallettaDx ?? 0) : 0);

    if (larghezzaTotale < 2020) {
        throw {severity: "warning", message: "Larghezza foro non consentita"};
    }

    if (altezzaForo < 1900) {
        throw {severity: "warning", message: "Altezza foro non consentita"};
    }
}

function checkInstallazioneInLuce(configuration: DoorConfiguration) {

}

function checkInstallazioneOltreLuce(configuration: DoorConfiguration) {
    const {
        tipoMolle,
        tipoInstallazioneMontanteDx,
        tipoInstallazioneMontanteSx,
        tipoInstallazioneTraversa,
        altezzaArchitrave,
        larghezzaSpallettaDx,
        larghezzaSpallettaSx,
        isMotorizzata
    } = configuration;
    if (tipoInstallazioneTraversa == InstallationType.oltre_luce && altezzaArchitrave < 30) throw {severity: "warning", message: "Per l'installazione Oltre Luce in altezza, l'altezza dell'architrave deve essere almeno di 30 mm."}
    if (tipoInstallazioneMontanteDx == InstallationType.oltre_luce && larghezzaSpallettaDx < 30) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Destra deve essere almeno di 30 mm."}
    if (tipoInstallazioneMontanteSx == InstallationType.oltre_luce && larghezzaSpallettaSx < 30) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Sinistra deve essere almeno di 30 mm."}
}

function checkInstallazioneOltreLuceOverbas(configuration: DoorConfiguration) {
    const {
        tipoMolle,
        tipoInstallazioneMontanteDx,
        tipoInstallazioneMontanteSx,
        tipoInstallazioneTraversa,
        altezzaArchitrave,
        larghezzaSpallettaDx,
        larghezzaSpallettaSx,
        isMotorizzata
    } = configuration;

    if (tipoMolle == SpringType.torsione) {
        if (tipoInstallazioneTraversa == InstallationType.oltre_luce && altezzaArchitrave < 160) throw {severity: "warning", message: "Per l'installazione Oltre Luce in altezza, l'altezza dell'architrave deve essere almeno di 160 mm."}
        if (tipoInstallazioneMontanteDx == InstallationType.oltre_luce && larghezzaSpallettaDx < 70) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Destra deve essere almeno di 70 mm."}
        if (tipoInstallazioneMontanteSx == InstallationType.oltre_luce && larghezzaSpallettaSx < 70) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Sinistra deve essere almeno di 70 mm."}
    } else if (tipoMolle == SpringType.trazione) {
        if (tipoInstallazioneTraversa == InstallationType.oltre_luce) {
            if (!isMotorizzata && altezzaArchitrave < 30) {
                throw {severity: "warning", message: "Per l'installazione Oltre Luce in altezza, l'altezza dell'architrave deve essere almeno di 30 mm."};
            }

            if (isMotorizzata && altezzaArchitrave < 90) {
                throw {severity: "warning", message: "Per l'installazione motorizzata Oltre Luce in altezza, l'altezza dell'architrave deve essere almeno di 90 mm."};
            }
        }
        if (tipoInstallazioneMontanteDx == InstallationType.oltre_luce && larghezzaSpallettaDx < 70) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Destra deve essere almeno di 70 mm."}
        if (tipoInstallazioneMontanteSx == InstallationType.oltre_luce && larghezzaSpallettaSx < 70) throw {severity: "warning", message: "Per l'installazione Oltre Luce in larghezza, la larghezza della Spalletta Sinistra deve essere almeno di 70 mm."}
    }
}
